import { Fragment } from "react";
import { Check, X } from "lucide-react";

const STAGES = ["Quote Requested", "Payment Pending", "Processing", "Out for Delivery"];

// Map an order's DB status to the active timeline stage index (0-3).
export function orderStageIndex(o) {
  const f = (o.fulfillment_status || "processing").toLowerCase();
  const paid = o.payment_status === "paid";
  if (f === "dispatched" || f === "delivered") return 3;
  if (paid) return 2;
  return 1; // order placed, awaiting payment
}

export const OrderTimeline = ({ order }) => {
  const cancelled = (order.fulfillment_status || "").toLowerCase() === "cancelled";
  const delivered = (order.fulfillment_status || "").toLowerCase() === "delivered";
  const idx = orderStageIndex(order);

  if (cancelled) {
    return (
      <div data-testid="order-timeline-cancelled" className="flex items-center gap-2 rounded-sm border border-[#C0654E]/40 bg-[#C0654E]/10 px-4 py-3">
        <span className="grid place-items-center w-6 h-6 rounded-full bg-[#C0654E] text-black"><X size={14} /></span>
        <span className="font-grotesk text-[11px] uppercase tracking-[0.18em] text-[#C0654E]">Order cancelled</span>
      </div>
    );
  }

  const progressPct = (idx / (STAGES.length - 1)) * 75; // node centres span 12.5%–87.5%

  const etaLabel = (() => {
    if (delivered) return "Delivered";
    if (order.estimated_delivery) {
      const dt = new Date(order.estimated_delivery);
      if (!isNaN(dt)) return `Est. ${dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return null;
  })();

  const countdown = (() => {
    if (delivered || !order.estimated_delivery) return null;
    const dt = new Date(order.estimated_delivery);
    if (isNaN(dt)) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    dt.setHours(0, 0, 0, 0);
    const days = Math.round((dt - today) / 86400000);
    if (days > 1) return `arrives in ${days} days`;
    if (days === 1) return "arrives tomorrow";
    if (days === 0) return "arrives today";
    return "arriving soon";
  })();

  return (
    <div className="relative pt-1" data-testid="order-timeline">
      {/* track */}
      <div className="absolute top-5 left-[12.5%] right-[12.5%] h-0.5 bg-white/10" />
      <div className="absolute top-5 left-[12.5%] h-0.5 bg-[#B87333] transition-all duration-500" style={{ width: `${progressPct}%` }} />

      <div className="relative flex justify-between">
        {STAGES.map((label, i) => {
          const done = i < idx || (delivered && i <= idx);
          const current = i === idx && !delivered;
          return (
            <div key={label} className="flex flex-col items-center w-1/4 px-1">
              <span
                data-testid={`timeline-node-${i}`}
                data-state={done ? "done" : current ? "current" : "upcoming"}
                className={`grid place-items-center w-8 h-8 rounded-full text-[11px] font-grotesk transition-colors duration-300 ${
                  done
                    ? "bg-[#B87333] text-black"
                    : current
                      ? "bg-[#B87333] text-black ring-4 ring-[#B87333]/25 shadow-[0_0_20px_-4px_rgba(184,115,51,0.8)]"
                      : "bg-[#0e0e0e] border border-white/20 text-[#6f6f68]"
                }`}
              >
                {done ? <Check size={15} /> : i + 1}
              </span>
              <span
                className={`mt-3 text-center font-grotesk text-[9px] sm:text-[10px] uppercase tracking-[0.12em] leading-tight ${
                  current ? "text-[#F5F5F0]" : done ? "text-[#B87333]" : "text-[#6f6f68]"
                }`}
              >
                {label}
              </span>
              {i === 3 && etaLabel && (
                <span
                  data-testid="timeline-eta"
                  className={`mt-1 text-center font-body text-[10px] leading-tight ${delivered ? "text-[#2FB999]" : "text-[#B87333]"}`}
                >
                  {etaLabel}
                </span>
              )}
              {i === 3 && countdown && (
                <span data-testid="timeline-countdown" className="mt-0.5 text-center font-grotesk text-[9px] uppercase tracking-[0.1em] text-[#8A8A82] leading-tight">
                  {countdown}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
